import { indexedDBService } from "@/services/storage";
import { logger } from "@/utils/debug";
import type { UserFile } from "@/types/workspace/UserFile";
import type { UserFilesStore } from "@/types/stores/user-file";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

const initialState = {
  files: [],
  loading: true,
  error: null,
};

const removeFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
};

export const useUserFilesStore = create<UserFilesStore>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        getSessionId: () => {
          let sessionId = localStorage.getItem("my-data-session-id");
          if (!sessionId) {
            sessionId = `anon_${Date.now()}_${Math.random()
              .toString(36)
              .slice(2, 11)}`;
            localStorage.setItem("my-data-session-id", sessionId);
          }
          return sessionId;
        },

        refreshFiles: async () => {
          try {
            set(
              { loading: true, error: null },
              false,
              "user-files/refreshFiles"
            );

            // Load file IDs from IndexedDB
            const fileIds = await indexedDBService.listFileIds();

            // Get file metadata for each file ID
            const files: UserFile[] = [];
            for (const fileId of fileIds) {
              const fileData = await indexedDBService.getFileData(fileId);
              if (fileData) {
                files.push({
                  id: fileId,
                  name: fileData.metadata.fileName,
                  size: fileData.metadata.fileSize || 0,
                  type: fileData.metadata.fileType,
                  created_at: fileData.metadata.createdAt,
                  updated_at: fileData.metadata.createdAt,
                });
              }
            }

            set({ files, loading: false }, false, "user-files/refreshFiles");
          } catch (err) {
            logger.error("Failed to fetch files:", err);
            set(
              {
                error:
                  err instanceof Error ? err.message : "Failed to fetch files",
                loading: false,
              },
              false,
              "user-files/refreshFiles"
            );
          }
        },

        uploadFile: async (file: File) => {
          try {
            // Generate a unique file ID based on timestamp and random string
            const fileId = `file_${Date.now()}_${Math.random()
              .toString(36)
              .slice(2, 11)}`;

            const uploadedFile: UserFile = {
              id: fileId,
              name: removeFileExtension(file.name),
              size: file.size,
              type: file.type,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };

            // Note: The actual file data should be stored in IndexedDB by the upload handler
            // We're just creating the metadata here
            set(
              (state) => ({ files: [uploadedFile, ...state.files] }),
              false,
              "user-files/uploadFile"
            );

            return uploadedFile;
          } catch (err) {
            logger.error("Upload failed:", err);
            set(
              { error: err instanceof Error ? err.message : "Upload failed" },
              false,
              "user-files/uploadFile"
            );
            return null;
          }
        },

        deleteFile: async (fileId: string) => {
          try {
            // Delete from IndexedDB
            await indexedDBService.deleteFileData(fileId);

            set(
              (state) => ({
                files: state.files.filter((f) => f.id !== fileId),
              }),
              false,
              "user-files/deleteFile"
            );

            return true;
          } catch (err) {
            logger.error("Delete failed:", err);
            set(
              { error: err instanceof Error ? err.message : "Delete failed" },
              false,
              "user-files/deleteFile"
            );
            return false;
          }
        },
      }),
      {
        name: "user-files-store",
        partialize: (state) => ({
          files: state.files,
        }),
      }
    ),
    {
      name: "user-files-store",
    }
  )
);

export const useUserFiles = () => useUserFilesStore((state) => state.files);
export const useUserFilesLoading = () =>
  useUserFilesStore((state) => state.loading);
export const useUserFilesError = () =>
  useUserFilesStore((state) => state.error);
export const useRefreshFiles = () =>
  useUserFilesStore((state) => state.refreshFiles);
export const useUploadFile = () =>
  useUserFilesStore((state) => state.uploadFile);
export const useDeleteFile = () =>
  useUserFilesStore((state) => state.deleteFile);
export const useUserFilesContext = () => useUserFilesStore();

